import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { faqLd, howToLd, breadcrumbLd } from "../components/Seo";
import { fetchPhrasebookCountries, fetchPhrasebook } from "../lib/api";
import { track } from "../lib/analytics";
import {
  Translate,
  SpeakerHigh,
  SpeakerSlash,
  Copy,
  ShareNetwork,
  HandWaving,
  Lifebuoy,
  ForkKnife,
  Car,
  Bed,
  ShoppingBag,
  CurrencyCircleDollar,
  Compass,
  Smiley,
  HandPalm,
  CaretDown,
  Check,
  ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// LLM-returned icon strings → phosphor components
const ICONS = {
  wave: HandWaving,
  lifebuoy: Lifebuoy,
  bowl: ForkKnife,
  taxi: Car,
  bed: Bed,
  bag: ShoppingBag,
  money: CurrencyCircleDollar,
  compass: Compass,
  smile: Smiley,
  hand: HandPalm,
};

const CATEGORY_COLORS = [
  "#E0457B", "#3DB68B", "#FF7E4D", "#2A7FB8", "#7C6CE0",
  "#F4B942", "#3DB68B", "#E0457B", "#2A7FB8", "#7C6CE0",
];

export default function Phrasebook() {
  const { country: routeSlug } = useParams();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [slug, setSlug] = useState(routeSlug || "thailand");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(null);

  // Load country list once
  useEffect(() => {
    fetchPhrasebookCountries()
      .then(setCountries)
      .catch(() => toast.error("Couldn't load phrasebook languages"));
  }, []);

  // Whenever slug changes, fetch + update URL
  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchPhrasebook(slug)
      .then((d) => {
        setData(d);
        track("phrasebook_loaded", { country: slug, cached: d.cached });
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Couldn't load phrasebook";
        toast.error(msg);
        setData(null);
      })
      .finally(() => setLoading(false));
    // Sync URL without reloading
    if (routeSlug !== slug) navigate(`/phrasebook/${slug}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // TTS playback — uses browser Web Speech API with the country's BCP-47 lang code.
  // Picks the best available voice for that lang.
  const speak = useCallback(
    (text, key) => {
      if (muted) {
        toast.message("Speaker is muted");
        return;
      }
      if (!("speechSynthesis" in window)) {
        toast.error("Your browser doesn't support speech playback");
        return;
      }
      try {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text);
        u.lang = data?.lang_code || "en-IN";
        const voices = window.speechSynthesis.getVoices();
        const match =
          voices.find((v) => v.lang === u.lang) ||
          voices.find((v) => v.lang?.startsWith(u.lang.split("-")[0]));
        if (match) u.voice = match;
        u.rate = 0.9;
        u.pitch = 1.0;
        u.onstart = () => setSpeaking(key);
        u.onend = () => setSpeaking(null);
        u.onerror = () => setSpeaking(null);
        window.speechSynthesis.speak(u);
        track("phrase_played", { country: slug, lang: u.lang });
      } catch (e) {
        toast.error("Couldn't play this phrase");
      }
    },
    [muted, data, slug],
  );

  const copyPhrase = (p) => {
    const text = `${p.en} = ${p.native || ""} (${p.phonetic})`;
    navigator.clipboard.writeText(text);
    toast.success("Phrase copied");
    track("phrase_copied", { country: slug });
  };

  const share = () => {
    const url = `${window.location.origin}/phrasebook/${slug}`;
    const title = `30 must-know ${data?.language || ""} phrases for Indians · Flycher`;
    if (navigator.share) {
      navigator.share({ title, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied");
    }
    track("phrasebook_shared", { country: slug });
  };

  const totalPhrases =
    data?.categories?.reduce((acc, c) => acc + (c.phrases?.length || 0), 0) || 0;

  // Structured data — FAQ rich snippets for "how to say X in Y" + HowTo + Breadcrumb.
  // This is THE highest-leverage SEO play because Google rewards these exact queries
  // with FAQ rich results that take 60-70% of mobile screen real estate.
  const structuredData = useMemo(() => {
    const out = [
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Phrasebook", path: "/phrasebook" },
        ...(data?.country ? [{ name: data.country, path: `/phrasebook/${slug}` }] : []),
      ]),
    ];
    if (data?.categories?.length) {
      // FAQ: each phrase becomes "How do you say X in Language? → native + phonetic"
      const faqs = [];
      data.categories.forEach((cat) => {
        (cat.phrases || []).forEach((p) => {
          if (p?.en && p?.phonetic) {
            faqs.push({
              question: `How do you say "${p.en}" in ${data.language}?`,
              answer: `${p.en} = ${p.native || ""} — pronounced "${p.phonetic}" (Indian-English phonetics).${p.note ? " " + p.note : ""}`,
            });
          }
        });
      });
      if (faqs.length) out.push(faqLd(faqs));
      // HowTo: "Learn essential phrases in <language>"
      out.push(
        howToLd({
          name: `Learn ${totalPhrases} essential ${data.language} phrases for travel`,
          description: `Master the most useful ${data.language} phrases for your ${data.country} trip — with Indian-English friendly pronunciation.`,
          steps: data.categories.map((cat) => ({
            name: cat.name,
            text: (cat.phrases || [])
              .map((p) => `${p.en} = ${p.phonetic}`)
              .join("; "),
          })),
        }),
      );
    }
    return out;
  }, [data, slug, totalPhrases]);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F0]">
      <Seo
        title={
          data
            ? `${totalPhrases} must-know ${data.language} phrases for Indians`
            : "Travel phrasebook for Indians · Flycher"
        }
        description={
          data
            ? `Learn ${totalPhrases} essential ${data.language} phrases with Indian-English friendly pronunciation. Tap to hear each one — perfect for your ${data.country} trip.`
            : "30 critical travel phrases per country, with phonetic spelling tuned for Indian English speakers and audio playback."
        }
        path={`/phrasebook/${slug}`}
        jsonLd={structuredData}
      />
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-5 sm:px-8 pt-28 pb-20">
        <span className="kicker">— Travel phrasebook · Indian-friendly</span>
        <h1 className="font-serif text-4xl sm:text-6xl mt-2 text-slate-900 leading-tight">
          Speak the language,
          <br />
          <span className="italic" style={{ color: "#E0457B" }}>
            not the script.
          </span>
        </h1>
        <p className="text-slate-600 mt-4 max-w-2xl">
          30 must-know phrases per country, with phonetic spelling tuned for{" "}
          <strong>Indian English speakers</strong> (we write{" "}
          <span className="font-mono text-sm bg-slate-100 px-1.5 py-0.5 rounded">sah-wah-DEE krahp</span>, not{" "}
          <span className="font-mono text-sm bg-slate-100 px-1.5 py-0.5 rounded line-through">sawatdee krap</span>).
          Tap any phrase to hear it.
        </p>

        {/* Country picker */}
        <div data-testid="phrasebook-picker" className="mt-7 flex flex-wrap gap-2">
          {countries.map((c) => {
            const active = c.slug === slug;
            return (
              <Link
                key={c.slug}
                to={`/phrasebook/${c.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  setSlug(c.slug);
                }}
                data-testid={`phrasebook-pick-${c.slug}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs border transition ${
                  active
                    ? "text-white border-transparent shadow-sm"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
                style={active ? { background: "#E0457B" } : {}}
              >
                <span className="font-medium">{c.name}</span>
                <span className="text-[10px] opacity-80">· {c.language.split(" ")[0]}</span>
                {active && <Check size={11} weight="bold" />}
              </Link>
            );
          })}
        </div>

        {/* Toolbar */}
        {data && !loading && (
          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap rounded-2xl bg-white border border-slate-200 px-4 py-3">
            <div className="text-sm">
              <span className="text-slate-500">Showing</span>{" "}
              <strong className="text-slate-900">{totalPhrases}</strong>{" "}
              <span className="text-slate-500">
                {data.language} phrases for {data.country}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="phrasebook-mute"
                onClick={() => {
                  setMuted((m) => !m);
                  if (!muted) window.speechSynthesis?.cancel();
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              >
                {muted ? <SpeakerSlash size={13} weight="duotone" /> : <SpeakerHigh size={13} weight="duotone" />}
                {muted ? "Muted" : "Sound on"}
              </button>
              <button
                type="button"
                data-testid="phrasebook-share"
                onClick={share}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              >
                <ShareNetwork size={13} weight="duotone" />
                Share
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="mt-10 rounded-2xl bg-white border border-slate-100 p-8 text-center">
            <div className="w-10 h-10 mx-auto rounded-full border-2 border-[#E0457B] border-t-transparent animate-spin" />
            <p className="mt-4 text-sm text-slate-600 font-mono uppercase tracking-wider">
              Compiling phrases for {countries.find((c) => c.slug === slug)?.name || slug}…
            </p>
            <p className="mt-1 text-[11px] text-slate-400">
              First load can take ~10s; subsequent loads are instant.
            </p>
          </div>
        )}

        {/* Categories */}
        {data && !loading && (
          <section data-testid="phrasebook-content" className="mt-7 space-y-5">
            {data.categories.map((cat, ci) => {
              const Icon = ICONS[cat.icon] || HandWaving;
              const color = CATEGORY_COLORS[ci % CATEGORY_COLORS.length];
              return (
                <article
                  key={cat.name}
                  data-testid={`phrasebook-cat-${ci}`}
                  className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden"
                >
                  <header
                    className="px-5 py-3.5 flex items-center gap-3 border-b border-slate-100"
                    style={{ background: color + "0F" }}
                  >
                    <div
                      className="w-9 h-9 rounded-xl grid place-items-center"
                      style={{ background: color + "22", color }}
                    >
                      <Icon size={18} weight="duotone" />
                    </div>
                    <h2 className="font-serif text-xl text-slate-900">{cat.name}</h2>
                    <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-slate-400">
                      {cat.phrases.length} phrases
                    </span>
                  </header>
                  <ul>
                    {cat.phrases.map((p, pi) => {
                      const key = `${ci}-${pi}`;
                      const isSpeaking = speaking === key;
                      return (
                        <li
                          key={key}
                          data-testid={`phrasebook-phrase-${ci}-${pi}`}
                          className="px-5 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <button
                              type="button"
                              data-testid={`phrase-play-${ci}-${pi}`}
                              onClick={() => speak(p.native || p.en, key)}
                              className={`w-9 h-9 rounded-full grid place-items-center flex-shrink-0 transition ${
                                isSpeaking ? "animate-pulse" : ""
                              }`}
                              style={{ background: color, color: "#fff" }}
                              aria-label={`Play ${p.en}`}
                            >
                              <SpeakerHigh size={16} weight="fill" />
                            </button>

                            <div className="flex-1 min-w-0">
                              <p className="text-base text-slate-900 font-medium leading-tight">
                                {p.en}
                              </p>
                              {p.native && (
                                <p
                                  className="text-lg sm:text-xl mt-1 leading-snug"
                                  style={{ color }}
                                  lang={data.lang_code}
                                >
                                  {p.native}
                                </p>
                              )}
                              <p className="font-mono text-sm text-slate-700 mt-1 tracking-wide">
                                {p.phonetic}
                              </p>
                              {p.note && (
                                <p className="text-[11px] text-slate-500 mt-1.5 italic">
                                  💡 {p.note}
                                </p>
                              )}
                            </div>

                            <button
                              type="button"
                              data-testid={`phrase-copy-${ci}-${pi}`}
                              onClick={() => copyPhrase(p)}
                              className="w-8 h-8 rounded-full grid place-items-center flex-shrink-0 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
                              aria-label="Copy phrase"
                            >
                              <Copy size={14} weight="duotone" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </article>
              );
            })}
          </section>
        )}

        {/* CTA */}
        {data && !loading && (
          <section className="mt-10">
            <div className="rounded-2xl bg-slate-900 text-white p-6 sm:p-8 flex items-start sm:items-center gap-5 flex-col sm:flex-row justify-between">
              <div>
                <span className="kicker text-white/70">— Going to {data.country}?</span>
                <h3 className="font-serif text-2xl sm:text-3xl mt-2">
                  Now plan the trip and check the visa.
                </h3>
                <p className="text-sm text-slate-300 mt-1.5 max-w-xl">
                  Pair this phrasebook with Flycher's itinerary builder and visa wizard.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate(`/visa`)}
                  className="px-4 py-2.5 rounded-full text-xs font-semibold bg-white/15 text-white hover:bg-white/25"
                >
                  Visa
                </button>
                <button
                  onClick={() => navigate(`/reality-check`)}
                  className="px-4 py-2.5 rounded-full text-xs font-semibold bg-white/15 text-white hover:bg-white/25"
                >
                  Reality Check
                </button>
                <button
                  onClick={() => navigate(`/itinerary/${slug}`)}
                  className="px-4 py-2.5 rounded-full text-xs font-semibold text-white"
                  style={{ background: "#FF7E4D" }}
                >
                  Plan itinerary <ArrowRight size={12} weight="bold" className="inline ml-1" />
                </button>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
